import { memo, useMemo } from 'react'

import { FileItem, MIME_TYPES } from '@inno-sdks/files'
import { Button } from '@inno-ui/button'
import { ArrowDownRectangleLineIcon } from '@inno-ui/icons'
import { useGetFileItem } from 'modules/desktop/modules/files-integration/api'
import { useDownloadFile } from 'modules/desktop/modules/files-integration/hooks'
import {
  isClusterOfficeFile,
  sleep
} from 'modules/desktop/modules/files-integration/utils'
import { useTranslation } from 'react-i18next'

import { useItemModal } from '../../hooks/useItemModal'
import { ClusterOfficeEmbeddedView } from '../ClusterOffice'
import styles from '../Preview.module.scss'
import { AudioPreview } from './Audio'
import { HtmlPreview } from './Html'
import { ImagePreview } from './Image'
import { TextPreview } from './Text'
import { VideoPreview } from './Video'

export const FilePreview = memo(() => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop'
  })

  const { itemId, close } = useItemModal()

  const { data: item } = useGetFileItem<FileItem>(itemId)

  const downloadFile = useDownloadFile()

  const previewComponent = useMemo(() => {
    if (!item?.metaData.presignedUrl) {
      return null
    }

    if (Object.values(MIME_TYPES.image).includes(item.metaData.mimeType)) {
      return <ImagePreview />
    } else if (
      Object.values(MIME_TYPES.video).includes(item.metaData.mimeType)
    ) {
      return (
        <div className={styles.container}>
          <VideoPreview />
        </div>
      )
    } else if (item.metaData.mimeType === MIME_TYPES.plainDocument.html) {
      return <HtmlPreview />
    } else if (
      Object.values(MIME_TYPES.plainDocument).includes(item.metaData.mimeType)
    ) {
      return <TextPreview />
    } else if (
      Object.values(MIME_TYPES.audio).includes(item.metaData.mimeType)
    ) {
      return (
        <div className={styles.container}>
          <AudioPreview />
        </div>
      )
    }

    return null
  }, [item])

  const _isClusterOfficeFile = useMemo(
    () => !!item && isClusterOfficeFile(item),
    [item]
  )

  if (_isClusterOfficeFile) {
    return <ClusterOfficeEmbeddedView />
  }

  if (!previewComponent) {
    return (
      <div className={styles.container}>
        <span>{t('preview-unavailable')}</span>
        <Button
          label={t('download')}
          icon={ArrowDownRectangleLineIcon}
          onClick={async () => {
            downloadFile(item!.id)
            await sleep(500)
            close()
          }}
        />
      </div>
    )
  }

  return previewComponent
})
